import { FetchBaseQueryArgs } from '@reduxjs/toolkit/query/react';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { waitForTokenRefresh } from '@vs/oidc-client';

import { VsApiError } from './classes/VsApiError';
import { isErrorOfVsApi } from './errorHelper';
import { setErrorInstance } from './errorInstanceMap';
import { ReferenceOfMeta } from './type';
import { RtkErrorResponseOfVsApi } from './type';

type ExtraOptions = {
  isPublicEndpoint?: boolean;
  fallbackResponse?: unknown;
  isFallbackableError?: (
    error: FetchBaseQueryError | RtkErrorResponseOfVsApi
  ) => boolean;
  baseUrl?: string | ((meta: ReferenceOfMeta) => string | undefined);
};

type DynamicBaseQueryFn = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | RtkErrorResponseOfVsApi,
  ExtraOptions
>;

export function dynamicFetchBaseFactory(
  referenceOfMeta: ReferenceOfMeta,
  baseQueryOption?: FetchBaseQueryArgs
): DynamicBaseQueryFn {
  return async (args, api, extraOptions = {}) => {
    const {
      _baseUrl,
      _getTokenFunc,
      _prepareGlobalHeaders = () => {},
    } = referenceOfMeta;

    const customBaseUrl =
      typeof extraOptions.baseUrl === 'function'
        ? extraOptions.baseUrl(referenceOfMeta)
        : extraOptions.baseUrl;

    const bs = fetchBaseQuery({
      baseUrl: customBaseUrl ?? _baseUrl ?? '',
      prepareHeaders: async (headers, api) => {
        await _prepareGlobalHeaders(headers);

        if (!extraOptions.isPublicEndpoint) {
          await waitForTokenRefresh();
          if (typeof _getTokenFunc === 'function') {
            const token = await _getTokenFunc();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
          }
        }

        return headers;
      },
      credentials: 'omit',
      ...baseQueryOption,
    }) as DynamicBaseQueryFn;

    const result = await Promise.resolve(bs(args, api, extraOptions));

    if (result.error) {
      if (
        isErrorOfVsApi(result.error) &&
        extraOptions.fallbackResponse &&
        (typeof extraOptions.isFallbackableError === 'function'
          ? extraOptions.isFallbackableError(result.error)
          : result.error.status === 404)
      ) {
        // @ts-expect-error: this is run time mutation of RTK result, skip type issue
        result.data = extraOptions.fallbackResponse;
        // @ts-expect-error: this is run time mutation of RTK result, skip type issue
        delete result.error;
      } else {
        setErrorInstance(
          result.error,
          new VsApiError(result.error, args, api.endpoint)
        );
      }
    }
    return result;
  };
}
